
  export default {
    async mounted() {
      // Wait for element to be visible before initializing Cesium
      // This fixes TilesRenderer resolution warnings when viewer isn't visible yet
      this.waitForVisible(this.el, () => this.initCesium());
    },

    waitForVisible(element, callback) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            observer.disconnect();
            callback();
          }
        });
      });
      observer.observe(element);
    },

    async initCesium() {
      try {
        // Entrance coordinates (for ground-level view preset)
        const entranceLat = parseFloat(this.el.dataset.entranceLat);
        const entranceLng = parseFloat(this.el.dataset.entranceLng);
        // Building center coordinates (for overhead/3-quarter view presets)
        const buildingLat = parseFloat(this.el.dataset.buildingLat);
        const buildingLng = parseFloat(this.el.dataset.buildingLng);
        const apiKey = window.Enaia.googleMapsDynamicKey;
        const buildingAddress = this.el.dataset.buildingAddress;
        const t = (key) => window.I18n[key];
        const resetTooltip = t("Reset View");
        const zoomInTooltip = t("Zoom In");
        const zoomOutTooltip = t("Zoom Out");
        const groundViewTooltip = t("Ground View");
        const overheadViewTooltip = t("Overhead View");
        const viewUnavailable = t("3D View Unavailable");

        // Use entrance coordinates for initial view (backwards compatible)
        const lat = entranceLat;
        const lng = entranceLng;

        // Load CesiumJS from CDN (lazy load - only when modal opens)
        await this.loadCesium();

        // Load additional scripts after Cesium is available
        await this.loadCesiumNavigation();
        await this.loadCesiumUtils();
        this.loadCesiumMainCSS();

        // Create the Cesium viewer with optimized settings
        const viewer = new Cesium.Viewer(this.el, {
          baseLayerPicker: false,
          geocoder: false,
          homeButton: false,
          sceneModePicker: false,
          navigationHelpButton: false,
          timeline: false,
          animation: false,
          fullscreenButton: true,
          infoBox: false,
          selectionIndicator: false,
          imageryProvider: false,
          scene3DOnly: true,
          contextOptions: {
            webgl: { alpha: true, antialias: true }
          },
          moon: false
        });

        // Store viewer reference and coordinates for cleanup, updates, and preset views
        this.viewer = viewer;
        this.entranceLat = entranceLat;
        this.entranceLng = entranceLng;
        this.buildingLat = buildingLat;
        this.buildingLng = buildingLng;
        this.buildingAddress = buildingAddress;
        this.labels = {
          resetTooltip,
          zoomInTooltip,
          zoomOutTooltip,
          groundViewTooltip,
          overheadViewTooltip
        };

        // Hide credits container
        viewer.cesiumWidget._creditContainer.style.display = "none";

        // Hide the default globe - Google's tiles render their own ground
        viewer.scene.globe.show = false;
        viewer.scene.skyBox.show = false;
        viewer.scene.skyAtmosphere.show = false;
        viewer.scene.screenSpaceCameraController.enableCollisionDetection = true;

        // Set background color to dark (vendor's color)
        viewer.scene.backgroundColor = Cesium.Color.fromCssColorString('#0b1220');
        viewer.resize();

        // Set Google Maps API key before loading tiles
        Cesium.GoogleMaps.defaultApiKey = apiKey;

        // Add Google Photorealistic 3D Tiles
        const tileset = await Cesium.createGooglePhotorealistic3DTileset();
        viewer.scene.primitives.add(tileset);

        // Wait for tiles to load initially
        await Utils.waitTilesetFullyReady(tileset);

        // Initialize navigation controls
        const navOptions = {
          duration: 4,
          enableCompass: true,
          enableZoomControls: false,
          enableDistanceLegend: true,
          enableCompassOuterRing: true,
          resetFunction: () => Utils.smartFlyTo(viewer, lat, lng, buildingAddress),
          resetTooltip: resetTooltip,
          zoomInTooltip: zoomInTooltip,
          zoomOutTooltip: zoomOutTooltip
        };
        new CesiumNavigation(viewer, navOptions);

        // Create custom control panel with preset view buttons
        this.createControlPanel();

        // First fly to a general overhead position above the building
        // Then use SmartFly to approach the building properly
        viewer.camera.flyTo({
          destination: Cesium.Cartesian3.fromDegrees(lng, lat, 400),
          orientation: {
            heading: 0,
            pitch: Cesium.Math.toRadians(-60),
            roll: 0
          },
          duration: 2.0,
          // When the initial flight is complete, building is now loaded
          // SmartFly will calculate best approach based on building shape
          complete: () => {
            console.log("Initial approach complete. Calculating Smart Path...");
            Utils.smartFlyTo(viewer, lat, lng, buildingAddress);
          }
        });

      } catch (error) {
        console.error('Building3DView error:', error);
        const errorDiv = document.createElement('div');
        errorDiv.className = 'flex items-center justify-center h-full text-red-500';
        const innerDiv = document.createElement('div');
        innerDiv.className = 'text-center';
        const title = document.createElement('p');
        title.className = 'font-semibold mb-[5px]';
        title.textContent = viewUnavailable;
        const msg = document.createElement('p');
        msg.className = 'text-legacy-sm';
        msg.textContent = error.message;
        innerDiv.appendChild(title);
        innerDiv.appendChild(msg);
        errorDiv.appendChild(innerDiv);
        this.el.replaceChildren(errorDiv);
      }
    },

    loadCesium() {
      return new Promise((resolve, reject) => {
        if (window.Cesium) {
          resolve();
          return;
        }

        const css = document.createElement('link');
        css.rel = 'stylesheet';
        css.href = 'https://cesium.com/downloads/cesiumjs/releases/1.115/Build/Cesium/Widgets/widgets.css';
        document.head.appendChild(css);

        const script = document.createElement('script');
        script.src = 'https://cesium.com/downloads/cesiumjs/releases/1.115/Build/Cesium/Cesium.js';
        script.async = true;
        script.onload = () => {
          if (window.Cesium) {
            resolve();
          } else {
            reject(new Error('CesiumJS failed to initialize'));
          }
        };
        script.onerror = () => reject(new Error('Failed to load CesiumJS'));
        document.head.appendChild(script);
      });
    },

    loadCesiumNavigation() {
      return new Promise((resolve, reject) => {
        if (window.CesiumNavigation) {
          resolve();
          return;
        }
        const script = document.createElement('script');
        script.src = '/scripts/cesium-navigation.umd.js';
        script.async = true;
        script.onload = () => {
          if (window.CesiumNavigation) {
            resolve();
          } else {
            reject(new Error('CesiumNavigation failed to initialize'));
          }
        };
        script.onerror = () => reject(new Error('Failed to load CesiumNavigation'));
        document.head.appendChild(script);
      });
    },

    loadCesiumUtils() {
      return new Promise((resolve, reject) => {
        if (window.Utils) {
          resolve();
          return;
        }
        const script = document.createElement('script');
        script.src = '/scripts/cesium-utils.js';
        script.async = true;
        script.onload = () => {
          if (window.Utils) {
            resolve();
          } else {
            reject(new Error('CesiumUtils failed to initialize'));
          }
        };
        script.onerror = () => reject(new Error('Failed to load CesiumUtils'));
        document.head.appendChild(script);
      });
    },

    loadCesiumMainCSS() {
      if (document.querySelector('link[href="/scripts/cesium-main.css"]')) return;
      const css = document.createElement('link');
      css.rel = 'stylesheet';
      css.href = '/scripts/cesium-main.css';
      document.head.appendChild(css);
    },

    createControlPanel() {
      const panel = document.createElement('div');
      panel.id = 'cesiumControls';
      panel.className = 'nav-panel';

      const buttons = [
        { id: 'btnZoomIn', icon: 'zoom-in2', title: this.labels.zoomInTooltip },
        { id: 'btnZoomOut', icon: 'zoom-out', title: this.labels.zoomOutTooltip },
        { id: 'btnReset', icon: 'reset', title: this.labels.resetTooltip },
        { id: 'btnGround', icon: 'ground', title: this.labels.groundViewTooltip },
        { id: 'btnOverhead', icon: 'overhead', title: this.labels.overheadViewTooltip }
      ];

      buttons.forEach(btn => {
        const button = document.createElement('button');
        button.id = btn.id;
        button.className = 'nav-btn';
        button.title = btn.title;
        button.setAttribute('data-icon', btn.icon);
        panel.appendChild(button);
      });

      this.el.appendChild(panel);

      panel.querySelectorAll('[data-icon]').forEach(async (btn) => {
        const iconName = btn.getAttribute('data-icon');
        try {
          const svg = await fetch(`/cesium-icons/${iconName}.svg`).then(res => res.text());
          btn.innerHTML = svg;
        } catch (e) {
          console.warn(`Failed to load icon: ${iconName}`, e);
        }
      });

      panel.querySelector('#btnZoomIn').onclick = () => Utils.relativeZoom(this.viewer, 1/2);
      panel.querySelector('#btnZoomOut').onclick = () => Utils.relativeZoom(this.viewer, 2);
      panel.querySelector('#btnReset').onclick = () => Utils.smartFlyTo(this.viewer, this.entranceLat, this.entranceLng, this.buildingAddress);
      panel.querySelector('#btnGround').onclick = () => Utils.flyGroundView(this.viewer, this.entranceLat, this.entranceLng);
      panel.querySelector('#btnOverhead').onclick = () => Utils.flyOverheadView(this.viewer, this.buildingLat, this.buildingLng);
    },

    updated() {
      // Check if coordinates changed
      const newEntranceLat = parseFloat(this.el.dataset.entranceLat);
      const newEntranceLng = parseFloat(this.el.dataset.entranceLng);
      const newBuildingLat = parseFloat(this.el.dataset.buildingLat);
      const newBuildingLng = parseFloat(this.el.dataset.buildingLng);
      const newAddress = this.el.dataset.buildingAddress;

      if (this.entranceLat !== newEntranceLat || this.entranceLng !== newEntranceLng) {
        this.entranceLat = newEntranceLat;
        this.entranceLng = newEntranceLng;
        this.buildingLat = newBuildingLat;
        this.buildingLng = newBuildingLng;
        this.buildingAddress = newAddress;

        // Fly to new location using two-step approach
        if (this.viewer && window.Utils) {
          // First fly to overhead position, then SmartFly
          this.viewer.camera.flyTo({
            destination: Cesium.Cartesian3.fromDegrees(newEntranceLng, newEntranceLat, 400),
            orientation: {
              heading: 0,
              pitch: Cesium.Math.toRadians(-60),
              roll: 0
            },
            duration: 2.0,
            complete: () => {
              Utils.smartFlyTo(this.viewer, newEntranceLat, newEntranceLng, newAddress);
            }
          });
        }
      }
    },

    destroyed() {
      if (this.viewer) {
        this.viewer.destroy();
        this.viewer = null;
      }
    }
  }
